import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, FileText, Trash2, CheckCircle, Clock, AlertCircle, Plus, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

interface TerminologyTemplate {
  id: number;
  name: string;
  description: string;
  file_type: string;
  file_size: number;
  extracted_terms_count: number;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  created_by_name: string;
  definitions_count: number;
}

interface TerminologyTemplateUploadProps {
  projectId: number;
}

const TerminologyTemplateUpload: React.FC<TerminologyTemplateUploadProps> = ({ projectId }) => {
  const [templates, setTemplates] = useState<TerminologyTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    file: null as File | null
  });

  // Status polling ref
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const lastStatusRef = useRef<Map<number, string>>(new Map());

  // Load templates
  const loadTemplates = useCallback(async (showLoading = true) => {
    if (!projectId) return;

    try {
      if (showLoading) setIsLoading(true);
      
      const response = await api.get(`/api/terminology/${projectId}/templates`);
      if (response.data.success) {
        const newTemplates = response.data.data.templates;
        
        // Check for status changes and show toast notifications
        if (!showLoading) { // Only show status change toasts during polling
          newTemplates.forEach((template: TerminologyTemplate) => {
            const previousStatus = lastStatusRef.current.get(template.id);
            if (previousStatus && previousStatus !== template.processing_status) {
              switch (template.processing_status) {
                case 'completed':
                  toast.success(`Template "${template.name}" processing completed!`, {
                    duration: 5000,
                    icon: '✅'
                  });
                  break;
                case 'failed':
                  toast.error(`Template "${template.name}" processing failed`, {
                    duration: 5000,
                    icon: '❌'
                  });
                  break;
                case 'processing':
                  toast.loading(`Template "${template.name}" is processing...`, {
                    duration: 2000,
                    icon: '⏳'
                  });
                  break;
              }
            }
          });
        }
        
        // Update status tracking
        newTemplates.forEach((template: TerminologyTemplate) => {
          lastStatusRef.current.set(template.id, template.processing_status);
        });
        
        setTemplates(newTemplates);
        
        // Check if we need to continue polling
        const hasProcessing = newTemplates.some(
          (t: TerminologyTemplate) => t.processing_status === 'processing' || t.processing_status === 'pending'
        );
        
        if (hasProcessing) {
          startStatusPolling();
        } else {
          stopStatusPolling();
        }
      }
    } catch (error) {
      console.error('Error loading terminology templates:', error);
      if (showLoading) {
        toast.error('Failed to load templates');
      }
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [projectId]);

  // Start polling for status updates
  const startStatusPolling = useCallback(() => {
    if (pollingRef.current) return; // Already polling
    
    pollingRef.current = setInterval(() => {
      loadTemplates(false); // Load without showing loading indicator
    }, 3000); // Poll every 3 seconds
  }, [loadTemplates]);

  // Stop polling
  const stopStatusPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Manual refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadTemplates(false);
    toast.success('Templates refreshed');
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Initialize and cleanup
  useEffect(() => {
    loadTemplates();
    return () => stopStatusPolling(); // Cleanup on unmount
  }, [projectId, loadTemplates, stopStatusPolling]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF, Word, Excel, and CSV files are supported');
        return;
      }

      setUploadForm(prev => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, '') // Remove extension for default name
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Please select a file to upload');
      return;
    }

    if (!uploadForm.name.trim()) {
      toast.error('Please provide a template name');
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('template', uploadForm.file);
      formData.append('name', uploadForm.name.trim());
      formData.append('description', uploadForm.description.trim());

      const response = await api.post(`/api/terminology/${projectId}/upload-template`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Template uploaded successfully! Processing will begin shortly...', {
          duration: 6000,
          icon: '🚀'
        });
        
        // Reset form and close modal
        setUploadForm({ name: '', description: '', file: null });
        setShowUploadForm(false);
        
        // Immediately reload templates to show the new one
        await loadTemplates();
        
        // Start polling since we have a processing template
        startStatusPolling();
      }
    } catch (error: any) {
      console.error('Error uploading template:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Upload failed';
      
      toast.error(`Upload failed: ${errorMessage}`, {
        duration: 6000
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (templateId: number, templateName: string) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${templateName}"?\n\nThis will also delete all extracted definitions and cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const response = await api.delete(`/api/terminology/${projectId}/templates/${templateId}`);
      
      if (response.data.success) {
        toast.success(`Template "${templateName}" deleted successfully`);
        
        // Remove from local state immediately for better UX
        setTemplates(prev => prev.filter(t => t.id !== templateId));
        
        // Also remove from status tracking
        lastStatusRef.current.delete(templateId);
        
        // Reload to ensure data consistency
        await loadTemplates(false);
      }
    } catch (error: any) {
      console.error('Error deleting template:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Delete failed';
      
      toast.error(`Delete failed: ${errorMessage}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'processing':
        return <Clock className="h-5 w-5 text-yellow-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing...';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSupportedFormats = () => {
    return [
      { ext: 'PDF', desc: 'PDF documents with term definitions' },
      { ext: 'DOCX', desc: 'Word documents with glossaries' },
      { ext: 'XLSX', desc: 'Excel spreadsheets with term-definition columns' },
      { ext: 'CSV', desc: 'CSV files with structured terminology data' }
    ];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Clock className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Upload and Refresh buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Definitions & Acronyms Templates
          </h3>
          <p className="text-sm text-gray-600">
            Upload templates to automatically match terminology during document analysis
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
            title="Refresh templates status"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowUploadForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Upload Template</span>
          </button>
        </div>
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-lg font-semibold text-gray-900">
                  Upload Terminology Template
                </h4>
                <button
                  onClick={() => setShowUploadForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File *
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.csv"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supported: PDF, Word, Excel, CSV files (max 10MB)
                  </p>
                </div>

                {/* Template Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., Financial Terms Glossary"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Brief description of the terminology template"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowUploadForm(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={isUploading || !uploadForm.file}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    <span>{isUploading ? 'Uploading...' : 'Upload Template'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          📖 How to Create Terminology Templates
        </h4>
        <div className="text-sm text-blue-800 space-y-2">
          <p><strong>Supported Formats:</strong></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
            {getSupportedFormats().map((format, index) => (
              <div key={index}>
                <span className="font-medium">{format.ext}:</span> {format.desc}
              </div>
            ))}
          </div>
          <p className="mt-2">
            <strong>Tips:</strong> Use clear "Term: Definition" format or structured tables for best results.
          </p>
        </div>
      </div>

      {/* Templates List */}
      {templates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No terminology templates uploaded
          </h3>
          <p className="text-gray-600 mb-4">
            Upload terminology templates to enable automatic term matching during document analysis.
          </p>
          <button
            onClick={() => setShowUploadForm(true)}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Upload First Template
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((template) => (
            <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Header with name and status */}
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-medium text-gray-900">
                      {template.name}
                    </h4>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(template.processing_status)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(template.processing_status)}`}>
                        {getStatusText(template.processing_status)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Description */}
                  {template.description && (
                    <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  )}
                  
                  {/* File Details */}
                  <div className="flex items-center space-x-6 text-sm text-gray-500 mb-3">
                    <span className="flex items-center">
                      <FileText className="h-4 w-4 mr-1" />
                      {template.file_type.split('/').pop()?.toUpperCase()}
                    </span>
                    <span>Size: {formatFileSize(template.file_size)}</span>
                    <span>Terms: <strong>{template.extracted_terms_count || 0}</strong></span>
                    <span>By: {template.created_by_name}</span>
                    <span>{new Date(template.created_at).toLocaleDateString()}</span>
                  </div>

                  {/* Status Messages */}
                  {template.processing_status === 'completed' && template.extracted_terms_count > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        <span>
                          Successfully extracted <strong>{template.extracted_terms_count} terms</strong> for terminology matching
                        </span>
                      </div>
                    </div>
                  )}

                  {template.processing_status === 'processing' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                        <span>Processing template to extract terminology definitions...</span>
                      </div>
                    </div>
                  )}

                  {template.processing_status === 'failed' && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                      <div className="flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        <span>Failed to process template. Please check file format and try uploading again.</span>
                      </div>
                    </div>
                  )}

                  {template.processing_status === 'pending' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>Template uploaded successfully. Processing will begin shortly...</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Delete Button */}
                <div className="flex items-center">
                  <button
                    onClick={() => handleDelete(template.id, template.name)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors group"
                    title={`Delete ${template.name}`}
                  >
                    <Trash2 className="h-5 w-5 group-hover:scale-110 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Processing Info */}
      {templates.some(t => t.processing_status === 'processing' || t.processing_status === 'pending') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-blue-600 animate-spin mr-3" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Templates are being processed
              </p>
              <p className="text-xs text-blue-700">
                Status will update automatically when processing completes
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminologyTemplateUpload;